<template>
  <b-modal
    id="modal-event-system"
    ref="modal-event-system"
    :hide-footer="true"
    :hide-header="true"
    modal-class="modal-event-system"
    @shown="onShown"
    @hidden="onHidden"
  >
    <template slot="default">
      <div class="event-system-canvas" :style="canvasStyle">
        <!-- Progress sprites at native size, centred; the background's
             transparent window gives the shape (tube, circle, ...). -->
        <div
          v-if="capsuleBehindUrl && capsuleBehindSize"
          class="capsule-behind"
          :style="capsuleBehindStyle"
        ></div>
        <div
          v-if="progressFluidUrl && progressFluidSize"
          class="progress-fluid"
          :style="progressFluidStyle"
        ></div>
        <div
          v-if="backgroundUrl"
          class="background-layer"
          :style="{ backgroundImage: `url(${backgroundUrl})` }"
        ></div>

        <!-- One-shot sparkle burst at the fluid surface once it stops rising.
             Same art and motion as the card reward show. -->
        <div
          v-if="sparklesEnabled"
          class="fluid-sparkles"
          :style="{ top: surfaceTop }"
        >
          <img
            v-for="n in 3"
            :key="n"
            ref="sparkles"
            :src="sparkleImg"
            alt=""
            class="fluid-sparkle"
            :class="`fluid-sparkle-${n}`"
          />
        </div>

        <div v-if="showProgressText" class="progress-label">
          {{ percentLabel }}
        </div>

        <a
          href="#"
          class="close-button"
          aria-label="close"
          @click.prevent="hide"
        ></a>
        <a
          href="#"
          class="info-button"
          aria-label="info"
          @click.prevent="onInfo"
        ></a>

        <div class="block timer-block" :style="timerStyle">
          <countdown-digital
            v-if="timerEndsAt"
            :time="timerEndsAt"
            :two-segments="true"
            class="timer-text"
          />
        </div>

        <div v-if="logoUrl" class="block logo-block">
          <img :src="logoUrl" :alt="title" class="logo-img" />
        </div>

        <div
          v-if="taskBoxUrl"
          class="task-box"
          :style="{ backgroundImage: `url(${taskBoxUrl})` }"
        ></div>
        <div class="description-block">
          <div class="title-band">
            <p
              v-if="taskTitleHtml"
              class="task-title"
              v-html="taskTitleHtml"
            ></p>
          </div>
          <div class="flavour-area">
            <p
              v-if="taskFlavourHtml"
              class="description"
              v-html="taskFlavourHtml"
            ></p>
          </div>
        </div>

        <!-- Right column panel; its top pill is the free reward's frame. -->
        <div
          v-if="rewardPanelUrl"
          class="reward-panel"
          :style="{ backgroundImage: `url(${rewardPanelUrl})` }"
        ></div>
        <div class="block free-area" :class="{ claimed: showFreeCollected }">
          <div v-if="freeTitle" class="box-title">{{ freeTitle }}</div>
          <div v-if="showFreeCollected" class="box-content collected-label">
            <img
              v-if="collectedIconUrl"
              :src="collectedIconUrl"
              alt=""
              class="collected-icon"
              :class="{ 'pop-in': popTick.free }"
            />
            {{ $t("seasonpass.button.collected") }}
          </div>
          <div class="box-content rewards">
            <div
              v-for="reward in freeRewardList"
              :key="reward.id"
              class="reward"
            >
              <img
                v-if="rewardIcon(reward)"
                :src="rewardIcon(reward)"
                alt=""
                class="reward-icon"
                :class="`icon-${rewardWallet(reward)}`"
              />
              <span class="reward-amount">
                {{ formatNumber(reward.amount) }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="block premium-box"
          :class="{ upgraded: isPremiumUser, claimed: showPremiumCollected }"
          :style="premiumBoxStyle"
        >
          <div v-if="premiumTitle" class="box-title">{{ premiumTitle }}</div>
          <div v-if="showPremiumCollected" class="box-content collected-label">
            <img
              v-if="collectedIconUrl"
              :src="collectedIconUrl"
              alt=""
              class="collected-icon"
              :class="{ 'pop-in': popTick.premium }"
            />
            {{ $t("seasonpass.button.collected") }}
          </div>
          <div
            class="box-content rewards"
            :class="`reward-count-${Math.min(premiumRewardList.length, 4)}`"
          >
            <div
              v-for="reward in premiumRewardList.slice(0, 4)"
              :key="reward.id"
              class="reward"
            >
              <img
                v-if="rewardIcon(reward)"
                :src="rewardIcon(reward)"
                alt=""
                class="reward-icon"
                :class="`icon-${rewardWallet(reward)}`"
              />
              <span class="reward-amount">
                {{ formatNumber(reward.amount) }}
              </span>
            </div>
          </div>
        </div>

        <button
          v-if="primaryAction"
          class="action-button primary-action"
          :style="premiumButtonStyle"
          :disabled="busy"
          @click="runAction(primaryAction)"
        >
          {{ actionLabel(primaryAction) }}
          <span v-if="showClaimDot" class="claim-bubble"></span>
        </button>
        <!-- Nothing left to do: the button art stays, faded, as "Claimed". -->
        <div
          v-else-if="showClaimedButton"
          class="action-button primary-action claimed-action"
          :style="premiumButtonStyle"
        >
          {{ $t("events.winter.collect_button") }}
        </div>

        <a
          v-if="secondaryAction"
          href="#"
          class="secondary-action"
          :class="{ disabled: busy }"
          @click.prevent="!busy && runAction(secondaryAction)"
        >
          {{ $t("event.button.claim_free") }}
        </a>

        <modal-toaster ref="toaster" />
      </div>

      <!-- Last upsell before the free reward is booked on its own. -->
      <modal-event-stumble
        :event="event"
        :premium-price="premiumPrice"
        :busy="busy"
        @claim-free="confirmFreeClaim"
        @get-premium="onStumblePremium"
      />
    </template>
  </b-modal>
</template>

<script>
import CountdownDigital from "@/components/base/countdown-digital";
import ModalEventStumble from "./modal-event-stumble.vue";
import { offerMethodsMixin } from "@/components/shop/offers/pay/offerMethodsMixin.js";
import ModalToaster from "@/components/base/modal-toaster.vue";
import AnimationFullRewardEarned from "@/components/animation/animation-full-reward-earned";
import { freeClaim } from "@/components/shop/offers/mixins/freeClaim";
import i18next from "i18next";
import { afterQueueAnimation } from "@/utils/afterQueueAnimation.js";
import {
  EVENT_ACTION,
  PREMIUM_MODE,
  availableActions,
  canClaim,
  configText,
  displayConfig,
  formatDescription,
  freeRewards,
  isClaimed,
  isPremium,
  isPremiumClaimed,
  premiumMode,
  premiumRewards,
  progressPercent,
  resolveAssetUrl,
  showClaimBubble,
  taskKey,
  timerTarget,
} from "@/utils/eventSystem.js";

// Background art's native size.
const CANVAS_W = 2340;
const CANVAS_H = 1080;

const centredBox = ({ w, h }) => ({
  left: `${((CANVAS_W - w) / 2 / CANVAS_W) * 100}%`,
  top: `${((CANVAS_H - h) / 2 / CANVAS_H) * 100}%`,
  width: `${(w / CANVAS_W) * 100}%`,
  height: `${(h / CANVAS_H) * 100}%`,
});

const loadImageSize = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

export default {
  name: "ModalEventSystem",
  components: { CountdownDigital, ModalEventStumble, ModalToaster },
  mixins: [offerMethodsMixin, AnimationFullRewardEarned, freeClaim],
  data() {
    return {
      // Which event this modal is showing. Defaults to the first active one.
      eventKey: null,
      busy: false,
      // What the fluid and label show -- tweened towards `percent`, from 0 on
      // every open.
      shownPercent: 0,
      fillTween: null,
      sparkleTimeline: null,
      // Hidden for now; flip to true to bring the burst back.
      sparklesEnabled: false,
      sparkleImg: require("@/assets/img/bonus-slot/sparkles@3x.png"),
      // Boxes whose claim is booked but not revealed yet -- they keep their
      // pre-claim look until the reward show has finished.
      revealPending: { free: false, premium: false },
      // Boxes whose checkmark pops in once that reward show has finished.
      popTick: { free: false, premium: false },
      capsuleBehindSize: null,
      progressFluidSize: null,
    };
  },
  computed: {
    // `eventKey`, else the first active event.
    event() {
      const byKey = this.$store.getters["eventSystem/eventByKey"];
      if (this.eventKey != null) return byKey(this.eventKey);
      return this.$store.getters["eventSystem/badgeEvents"][0] || null;
    },
    // ACP display_config.style["border-color"], same as the offer chain.
    canvasStyle() {
      const style = displayConfig(this.event).style || {};
      const vars = {
        "--event-border-color": style["border-color"],
        "--timer-color": style["timer-color"],
        "--task-title-color": style["task-title-color"],
        "--description-color": style["description-color"],
        "--description-highlight-color": style["description-highlight-color"],
        "--reward-title-color": style["reward-title-color"],
        "--reward-premium-title-color": style["reward-premium-title-color"],
        "--premium-button-title-color": style["premium-button-title-color"],
      };
      return Object.fromEntries(Object.entries(vars).filter(([, v]) => v));
    },
    freeTitle() {
      return this.crowdinText("event.reward.free_title");
    },
    premiumTitle() {
      return this.crowdinText("event.reward.premium_title");
    },
    isPremiumClaimed() {
      return isPremiumClaimed(this.event);
    },
    // Stays set after a later premium purchase.
    isFreeClaimed() {
      return isClaimed(this.event);
    },
    showFreeCollected() {
      return this.isFreeClaimed && !this.revealPending.free;
    },
    showPremiumCollected() {
      return this.isPremiumClaimed && !this.revealPending.premium;
    },
    // Claimed and no action left (premium claimed, or not on offer).
    showClaimedButton() {
      return (
        !this.primaryAction &&
        this.showFreeCollected &&
        !this.revealPending.premium
      );
    },
    title() {
      return (this.event && this.event.title) || "";
    },
    // Task title: always Crowdin `<task key>` itself, target highlighted.
    taskTitleHtml() {
      return formatDescription(this.taskText());
    },
    // Flavour: ACP display_config.text.description_flavour, else Crowdin
    // `<task key>.flavour`.
    taskFlavourHtml() {
      const locale = this.$store.state.locale;
      return formatDescription(
        configText(this.event, "text", "description_flavour", locale) ||
          this.taskText("flavour")
      );
    },
    logoUrl() {
      return resolveAssetUrl(this.event, "logo");
    },
    backgroundUrl() {
      return this.assetUrl("background");
    },
    capsuleBehindUrl() {
      return (
        this.assetUrl("progress_bar_background") ||
        this.assetUrl("capsule_behind")
      );
    },
    progressFluidUrl() {
      return this.assetUrl("progress_bar");
    },
    capsuleBehindStyle() {
      return {
        ...centredBox(this.capsuleBehindSize),
        backgroundImage: `url(${this.capsuleBehindUrl})`,
      };
    },
    // Hide everything above the filled part.
    progressFluidStyle() {
      return {
        ...centredBox(this.progressFluidSize),
        backgroundImage: `url(${this.progressFluidUrl})`,
        clipPath: `inset(${100 - this.shownPercent}% 0 0 0)`,
      };
    },
    // Where the fluid ends, as a share of the canvas height.
    surfaceTop() {
      const h = this.progressFluidSize ? this.progressFluidSize.h : 0;
      const y = (CANVAS_H - h) / 2 + h * (1 - this.percent / 100);
      return `${(y / CANVAS_H) * 100}%`;
    },
    premiumPackage() {
      return (
        (this.event && this.event.premium && this.event.premium.package) || null
      );
    },
    // Web price of the premium package, e.g. "2,99€". Empty for currency mode.
    premiumPrice() {
      if (premiumMode(this.event) !== PREMIUM_MODE.PAYMENT) return "";
      return this.formatPrice(this.premiumPackage);
    },
    // ACP display_config.style["show-progress-text"]; shown unless false.
    showProgressText() {
      const style = displayConfig(this.event).style || {};
      const show = style["show-progress-text"];
      return show !== false && show !== "false";
    },
    // Floored, so 99.6% never reads as done.
    percentLabel() {
      return `${Math.floor(this.shownPercent)}%`;
    },
    timerBackgroundUrl() {
      return resolveAssetUrl(this.event, "timer_background");
    },
    timerStyle() {
      if (!this.timerBackgroundUrl) return {};
      return { backgroundImage: `url(${this.timerBackgroundUrl})` };
    },
    timerEndsAt() {
      return timerTarget(this.event);
    },
    percent() {
      return progressPercent(this.event);
    },
    isPremiumUser() {
      return isPremium(this.event);
    },
    collectedIconUrl() {
      return this.assetUrl("premium_unlocked");
    },
    taskBoxUrl() {
      return this.assetUrl("task_box_background");
    },
    rewardPanelUrl() {
      return this.assetUrl("reward_box_background");
    },
    premiumBoxStyle() {
      const url = this.assetUrl(
        this.isPremiumUser
          ? "reward_box_premium_upgraded"
          : "reward_box_premium"
      );
      return url ? { backgroundImage: `url(${url})` } : {};
    },
    // Hover art is swapped in through a CSS variable.
    premiumButtonStyle() {
      const url = this.assetUrl("premium_button");
      const hover = this.assetUrl("premium_button_hover") || url;
      if (!url) return {};
      return {
        "--button-bg": `url(${url})`,
        "--button-bg-hover": `url(${hover})`,
      };
    },
    freeRewardList() {
      return freeRewards(this.event);
    },
    premiumRewardList() {
      return premiumRewards(this.event);
    },
    actions() {
      return availableActions(this.event);
    },
    primaryAction() {
      return this.actions[0] || null;
    },
    // "or claim free reward" link below the button.
    secondaryAction() {
      return this.actions[1] || null;
    },
    showClaimDot() {
      return showClaimBubble(this.event);
    },
  },
  watch: {
    capsuleBehindUrl: {
      immediate: true,
      handler(url) {
        this.readSpriteSize(url, "capsuleBehindSize");
      },
    },
    progressFluidUrl: {
      immediate: true,
      handler(url) {
        this.readSpriteSize(url, "progressFluidSize");
      },
    },
    // A refetch while the modal is open (e.g. after a claim) moves the fluid
    // on from where it is, not from 0.
    percent() {
      if (this.fillTween || this.shownPercent > 0) this.animateFill();
    },
  },
  created() {
    // App.vue reopens the bought event after a premium purchase.
    this.$root.$on("event-system-open", this.openEvent);
  },
  beforeDestroy() {
    this.stopFill();
    this.$root.$off("event-system-open", this.openEvent);
  },
  methods: {
    assetUrl(key) {
      return resolveAssetUrl(this.event, key);
    },

    // Crowdin `<task key>` (title) or `<task key>.<part>`, with the target as
    // `{0}`; "" if missing.
    taskText(part) {
      const base = taskKey(this.event);
      const key = base && (part ? `${base}.${part}` : base);
      if (!key || !i18next.exists(key)) return "";
      return this.$t(key, { 0: `*${this.formatNumber(this.event.target)}*` });
    },
    // Crowdin text when the key exists, else empty (never the raw key).
    crowdinText(translationKey) {
      return i18next.exists(translationKey) ? this.$t(translationKey) : "";
    },

    hide() {
      this.$bvModal.hide("modal-event-system");
    },

    onShown() {
      this.closeStumble();
      this.stopFill();
      this.shownPercent = 0;
      // Explainer stacks on top on the player's first open (popup-over-popup).
      this.$eventBus.$emit("explainer:show", {
        feature: "eventsystem",
        firstOpen: true,
      });
      this.$store.dispatch("eventSystem/fetchActiveEvents").then(() => {
        // Opening the event clears the badge's red bubble.
        const key = this.event && (this.event.slug || this.event.id);
        if (key != null) this.$store.dispatch("eventSystem/markSeen", key);

        this.animateFill();
      });
    },

    readSpriteSize(url, field) {
      this[field] = null;
      if (!url) return;
      loadImageSize(url).then((size) => {
        const current =
          field === "progressFluidSize"
            ? this.progressFluidUrl
            : this.capsuleBehindUrl;
        if (url === current) this[field] = size;
      });
    },

    openEvent(key) {
      this.eventKey = key;
      this.$bvModal.show("modal-event-system");
    },

    onHidden() {
      this.closeStumble();
      this.busy = false;
      this.revealPending = { free: false, premium: false };
      this.popTick = { free: false, premium: false };
      // Back to the first active event for the next badge open.
      this.eventKey = null;
      this.stopFill();
      this.shownPercent = 0;
    },

    // Tween the fluid from wherever it is now to the real percentage.
    animateFill() {
      this.stopFill();
      this.fillTween = this.gsap.to(this, {
        shownPercent: this.percent,
        duration: 1.2,
        ease: "power2.out",
        onComplete: () => {
          this.fillTween = null;
          this.playSparkles();
        },
      });
    },

    stopFill() {
      if (this.fillTween) this.fillTween.kill();
      this.fillTween = null;
      this.stopSparkles();
    },

    // Motion from animation-full-reward-earned's sparkle timeline: fade in,
    // drift up, fade out. Offsets are % of each sparkle's own size, so the
    // burst scales with the modal.
    playSparkles() {
      this.stopSparkles();
      const sparkles = this.$refs.sparkles || [];
      if (!this.sparklesEnabled || !sparkles.length || this.percent <= 0)
        return;

      const paths = [
        { x: -5, y: -30, at: 0 },
        { x: 25, y: -60, at: 0.15 },
        { x: -30, y: -45, at: 0.3 },
      ];
      const timeline = this.gsap.timeline();
      sparkles.forEach((el, i) => {
        const path = paths[i];
        timeline
          .fromTo(
            el,
            { opacity: 0, xPercent: -50, yPercent: -50 },
            { opacity: 1, duration: 0.2, ease: "none" },
            path.at
          )
          .to(
            el,
            {
              xPercent: -50 + path.x,
              yPercent: -50 + path.y,
              duration: 0.6,
              ease: "power1.out",
            },
            path.at
          )
          .to(el, { opacity: 0, duration: 0.3, ease: "none" }, path.at + 0.3);
      });
      this.sparkleTimeline = timeline;
    },

    stopSparkles() {
      if (this.sparkleTimeline) this.sparkleTimeline.kill();
      this.sparkleTimeline = null;
      const sparkles = this.$refs.sparkles || [];
      if (sparkles.length) this.gsap.set(sparkles, { opacity: 0 });
    },
    onInfo() {
      this.$eventBus.$emit("explainer:show", { feature: "eventsystem" });
    },

    // Always Crowdin.
    actionLabel(action) {
      const getPremium = this.crowdinText("event.button.get_premium");
      const claim = this.crowdinText("event.button.claim_rewards");
      const labels = {
        [EVENT_ACTION.OPT_IN]: "join event",
        [EVENT_ACTION.GET_PREMIUM]: this.premiumPrice
          ? `${getPremium} ${this.premiumPrice}`
          : getPremium,
        [EVENT_ACTION.CLAIM_FREE]: claim,
        [EVENT_ACTION.CLAIM_REWARDS]: claim,
      };
      return labels[action] || action;
    },

    rewardIcon(reward) {
      const asset = (reward && reward.asset) || {};
      return (asset.image_urls && asset.image_urls.image_url) || "";
    },
    rewardWallet(reward) {
      const asset = (reward && reward.asset) || {};
      return asset.wallet || "";
    },

    runAction(action) {
      if (!this.event || this.busy) return;

      // Free-only claim goes through the stumble screen first.
      if (action === EVENT_ACTION.CLAIM_FREE) {
        this.$bvModal.show("modal-event-stumble");
        return;
      }

      // Below 100% the claim can only fail -- skip the round trip.
      if (action === EVENT_ACTION.CLAIM_REWARDS && !canClaim(this.event)) {
        this.$refs.toaster.show(this.$t("event.claim_locked"));
        return;
      }

      if (action === EVENT_ACTION.GET_PREMIUM) {
        this.buyPremium();
        return;
      }

      const key = this.event.slug || this.event.id;
      if (action === EVENT_ACTION.OPT_IN) {
        this.dispatchAction("eventSystem/optIn", key);
        return;
      }
      if (action === EVENT_ACTION.CLAIM_REWARDS) {
        this.claim(key);
      }
    },

    closeStumble() {
      this.$bvModal.hide("modal-event-stumble");
    },

    // "Thanks, I'm fine" on the stumble screen: book the free reward alone.
    confirmFreeClaim() {
      this.closeStumble();
      const key = this.event && (this.event.slug || this.event.id);
      if (key != null) this.claim(key);
    },

    // "Give me everything!" on the stumble screen.
    onStumblePremium() {
      this.closeStumble();
      this.buyPremium();
    },

    buyPremium() {
      const key = this.event.slug || this.event.id;

      if (premiumMode(this.event) === PREMIUM_MODE.CURRENCY) {
        this.closeStumble();
        this.dispatchAction("eventSystem/upgradeToPremium", key);
        return;
      }

      if (premiumMode(this.event) === PREMIUM_MODE.PAYMENT) {
        this.openPremiumPayment();
      }
    },

    // Real money: hand `premium.package` to the shared v2 payment modal, the
    // same way an offer-chain package is bought (chainPurchase.js). There is
    // no offer around it.
    openPremiumPayment() {
      const payPackage = this.premiumPackage;
      const prices = payPackage && payPackage.sku && payPackage.sku.prices;
      if (!prices || !prices.length) return;

      const webPrice = prices.find((p) => p.platform === "web");
      const packageData = {
        id: payPackage.id,
        package: {
          id: payPackage.id,
          name: payPackage.name,
          slug: payPackage.slug || "event-premium",
          price: webPrice && webPrice.price,
          items: payPackage.assets || [],
        },
        // The package grants premium, not currency -- nothing to list.
        rewards: {},
        category: "Event",
        variant: "EventPremium",
        customName: this.localizedPackageName(payPackage) || this.title,
        customImg: this.logoUrl || "",
        v2Package: payPackage,
        // The backend types `$offer_id` as int and 500s on null; an event
        // package has no offer.
        v2OfferId: 0,
        eventId: this.event.id,
      };

      this.$store.commit("shop/setShopChosenPackage", packageData);
      this.$bvModal.show("modal-payment");
    },

    claim(key) {
      const expected = this.claimableRewards();
      const claiming = {
        free: !this.isFreeClaimed,
        premium: this.isPremiumUser && !this.isPremiumClaimed,
      };
      this.revealPending = claiming;
      this.busy = true;

      this.$store
        .dispatch("eventSystem/claim", key)
        .then((data) => {
          afterQueueAnimation(this.$store, () => this.reveal(claiming));
          this.playRewardAnimation((data && data.rewards) || expected);
          this.$store.dispatch("user/updateUser");
        })
        .catch((err) => {
          this.revealPending = { free: false, premium: false };
          this.showError(err);
        })
        .finally(() => {
          this.busy = false;
        });
    },

    reveal(claimed) {
      this.revealPending = { free: false, premium: false };
      this.popTick = claimed;
    },

    claimableRewards() {
      const rewards = isClaimed(this.event) ? [] : [...this.freeRewardList];
      if (isPremium(this.event)) rewards.push(...this.premiumRewardList);
      return rewards;
    },

    playRewardAnimation(rewards) {
      const assets = rewards.map((reward) => {
        const asset = reward.asset || {};
        const booked = reward.booked || {};
        return {
          asset_type:
            booked.type ||
            (asset.payload && asset.payload.type) ||
            asset.wallet,
          amount: booked.amount || reward.amount,
          card_id: reward.card_id ?? asset.card_id,
        };
      });
      const animation = this.buildRewardAnimationArray(assets);
      if (animation.length) this.AnimationFullRewardEarnedAnimate(animation);
    },

    // 422s carry an EVENT_ERROR id that doubles as the translation key.
    showError(err) {
      const id = err && err.data && err.data.id;
      if (id && i18next.exists(id)) this.$refs.toaster.show(this.$t(id));
    },

    dispatchAction(action, key) {
      this.busy = true;
      this.$store
        .dispatch(action, key)
        .catch(this.showError)
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style lang="scss">
#modal-event-system * {
  font-family: "Roboto", sans-serif;
}

.modal-event-system {
  // Top-aligned like the offer chain / season pass modals. 1400px wide, but
  // never wider than the viewport, nor taller -- the canvas is 2340 x 1080.
  .modal-dialog {
    width: 100%;
    max-width: min(87.5rem, 100vw - 2rem, (100vh - 100px) * 2340 / 1080);
    padding-top: 50px;
  }

  .modal-content {
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .modal-body {
    padding: 0;
  }
}
</style>

<style scoped lang="scss">
// Positions are `doc pixels / canvas dimension`, matching
// `docs/event-system.md` 6.2.
$canvas-w: 2340;
$canvas-h: 1080;
// Margin left of the central 1920 safe zone: (2340 - 1920) / 2.
$safe-x: 210;
// Right column offsets from 6.2, fitted to the reward panel art.
// $column-lift moves the premium box, button and link up together.
$column-lift: 30;
$reward-dy: 48;
$button-dy: 30 - $column-lift;
$premium-dy: $reward-dy + 10 - $column-lift;
// Free reward = the pill at the top of the panel art (its chrome ring runs
// 7..203 px into the 816 px image placed at y 162).
$free-top: 169;
$free-h: 196;
$left-x: 260;
$task-w: 457;

$timer-scale: 0.85;
$timer-w: 296 * $timer-scale;
$timer-h: 130 * $timer-scale;

// Canvas px -> CSS length that scales with the modal (canvas is the size
// container), so text and icons keep their proportion to the art.
@function cpx($px) {
  @return calc(#{$px} / #{$canvas-w} * 100cqw);
}

.event-system-canvas {
  position: relative;
  width: 100%;
  aspect-ratio: 2340 / 1080;
  container-type: inline-size;
  border: 5px solid var(--event-border-color, #{$text-accent});
  box-sizing: border-box;
  background-color: #000000;
  border-radius: 0.75rem;
  overflow: hidden;
  color: white;
  font-weight: 500;

  // Centred, so any crop eats both edges evenly and the 1920 safe zone stays.
  .background-layer {
    position: absolute;
    inset: 0;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }

  .capsule-behind,
  .progress-fluid {
    position: absolute;
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  // `top` follows the fluid surface.
  .fluid-sparkles {
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    pointer-events: none;

    .fluid-sparkle {
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      transform: translate(-50%, -50%);
    }

    // 1020 x 741 art: one wide burst, two smaller accents.
    .fluid-sparkle-1 {
      width: cpx(180);
    }

    .fluid-sparkle-2,
    .fluid-sparkle-3 {
      width: cpx(100);
    }
  }

  .progress-label {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-weight: 700;
    font-size: cpx(40);
    text-shadow: 0 cpx(2) cpx(8) rgba(0, 0, 0, 0.85);
    pointer-events: none;
  }

  .block {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Drawn at the art's aspect ratio, so the pill is not squashed.
  .timer-block {
    left: calc(90 / #{$canvas-w} * 100%);
    top: calc(28px - #{$timer-h * 55 / 130} / #{$canvas-h} * 100%);
    width: calc(#{$timer-w} / #{$canvas-w} * 100%);
    height: calc(#{$timer-h} / #{$canvas-h} * 100%);
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;

    // Centred between the baked stopwatch's right edge (60) and the pill's
    // right edge (288); the bottom inset covers the drop shadow and the
    // digits' low-sitting glyph box.
    .timer-text {
      position: absolute;
      left: calc(60 / 296 * 100%);
      right: calc(8 / 296 * 100%);
      top: 0;
      bottom: calc(12 / 130 * 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: cpx(36 * $timer-scale);
      line-height: 1;
      letter-spacing: 0.02em;
      color: var(--timer-color, #ffffff);
      text-shadow: 0 cpx(2) cpx(6) rgba(0, 0, 0, 0.6);
      white-space: nowrap;
    }
  }

  .logo-block {
    left: calc((#{$left-x} + (#{$task-w} - 520) / 2) / #{$canvas-w} * 100%);
    top: calc(150 / #{$canvas-h} * 100%);
    width: calc(520 / #{$canvas-w} * 100%);
    height: calc(440 / #{$canvas-h} * 100%);

    .logo-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  // Panel art: 476 x 386 incl. an 8 px shadow margin, so the panel itself
  // lands at 457 x 368 @ $left-x 603.
  .task-box {
    position: absolute;
    left: calc((#{$left-x} - 8) / #{$canvas-w} * 100%);
    top: calc((603 - 8) / #{$canvas-h} * 100%);
    width: calc(476 / #{$canvas-w} * 100%);
    height: calc(386 / #{$canvas-h} * 100%);
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }
  .description-block {
    position: absolute;
    left: calc((#{$left-x} + 36) / #{$canvas-w} * 100%);
    top: calc((603 + 16) / #{$canvas-h} * 100%);
    width: calc((457 - 72) / #{$canvas-w} * 100%);
    height: calc(302 / #{$canvas-h} * 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    overflow: hidden;
    text-shadow: 0 cpx(2) cpx(6) rgba(0, 0, 0, 0.75);

    .title-band,
    .flavour-area {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .title-band {
      flex: 0 0 calc(70 / 302 * 100%);
      // Clear of the frame art's top edge.
      margin-top: cpx(20);
    }
    .flavour-area {
      flex: 1 1 0;
      min-height: 0;
      margin-top: cpx(16);
    }

    .task-title {
      margin: 0;
      color: var(--task-title-color, #ffffff);
      font-weight: 700;
      font-size: cpx(32);
      line-height: 1.1;
      text-transform: uppercase;
      text-shadow: 0 0 cpx(8) currentColor;

      ::v-deep b {
        color: var(--description-highlight-color, #{$text-accent});
      }
    }

    .description {
      margin: 0;
      color: var(--description-color, #ffffff);
      font-size: cpx(28);
      line-height: 1.35;
      // ACP line breaks (`\r\n`) come through as-is.
      white-space: pre-line;

      ::v-deep b {
        font-weight: 700;
        color: var(--description-highlight-color, #{$text-accent});
      }
    }
  }

  // Panel art: 576 x 816 incl. an 8 px shadow margin, so the panel itself
  // lands at 560 x 800 @ (1310 + $safe-x) 170.
  .reward-panel {
    position: absolute;
    left: calc((1310 + #{$safe-x} - 8) / #{$canvas-w} * 100%);
    top: calc(162 / #{$canvas-h} * 100%);
    width: calc(576 / #{$canvas-w} * 100%);
    height: calc(816 / #{$canvas-h} * 100%);
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  // 6.2 x coordinates are relative to the 1920 safe zone, hence $safe-x.
  // Sits on the panel's pill; the `/ 200` fractions below land on its band.
  .free-area {
    left: calc((1310 + #{$safe-x}) / #{$canvas-w} * 100%);
    top: calc(#{$free-top} / #{$canvas-h} * 100%);
    width: calc(560 / #{$canvas-w} * 100%);
    height: calc(#{$free-h} / #{$canvas-h} * 100%);

    // Title band of the frame art.
    .box-title {
      top: calc(45 / 200 * 100%);
      height: calc(50 / 200 * 100%);
      color: var(--reward-title-color, #ffffff);
    }

    // Below the title.
    .box-content {
      top: calc(95 / 200 * 100%);
      height: calc(75 / 200 * 100%);
    }

    .reward {
      flex-direction: row;
      gap: cpx(12);

      .reward-icon {
        width: cpx(60);
        height: cpx(60);
      }
    }
  }

  .claimed .rewards {
    opacity: 0.4;
  }

  .collected-label {
    z-index: 1;
    font-weight: 700;
    font-size: cpx(42);
    line-height: 1;
    white-space: nowrap;
    text-transform: capitalize;
    text-shadow: 0 cpx(3) cpx(6) rgba(0, 0, 0, 0.75);

    .collected-icon {
      position: absolute;
      left: 0;
      top: 50%;
      width: cpx(60);
      height: cpx(60);
      object-fit: contain;
      transform: translateY(-50%);
      &.pop-in {
        animation: tick-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
    }
  }

  // Free first, then premium.
  .premium-box .collected-icon.pop-in {
    animation-delay: 0.2s;
  }

  @keyframes tick-pop {
    from {
      opacity: 0;
      transform: translateY(-50%) scale(0) rotate(-30deg);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) scale(1) rotate(0);
    }
  }

  .premium-box {
    left: calc((1310 + #{$safe-x}) / #{$canvas-w} * 100%);
    top: calc((370 + #{$premium-dy}) / #{$canvas-h} * 100%);
    width: calc(560 / #{$canvas-w} * 100%);
    height: calc(400 / #{$canvas-h} * 100%);
    background-size: contain;

    .box-title {
      top: calc(50 / 400 * 100%);
      height: calc(55 / 400 * 100%);
      color: var(--reward-premium-title-color, #ffffff);
    }

    // Below the title.
    .box-content {
      top: calc(110 / 400 * 100%);
      height: calc(240 / 400 * 100%);
    }

    .reward .reward-icon {
      width: cpx(110);
      height: cpx(110);
    }

    .reward-count-2 .reward {
      .reward-icon {
        width: cpx(88);
        height: cpx(88);
      }

      .reward-amount {
        font-size: cpx(34);
      }
    }

    // Two per row, a third one centred below.
    .box-content.reward-count-3,
    .box-content.reward-count-4 {
      flex-wrap: wrap;
      gap: cpx(8) 0;
      align-content: center;

      .reward {
        width: 50%;

        .reward-icon {
          width: cpx(80);
          height: cpx(80);
        }

        .reward-amount {
          font-size: cpx(26);
        }
      }
    }
  }

  .free-area,
  .premium-box {
    display: block;

    .box-title {
      position: absolute;
      left: 10%;
      right: 10%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: cpx(36);
      line-height: 1;
      text-transform: uppercase;
      white-space: nowrap;
      text-shadow: 0 0 cpx(8) currentColor;
    }
    border-radius: 0;
    background-position: center;
    background-repeat: no-repeat;

    .box-content {
      position: absolute;
      left: 10%;
      right: 10%;
      display: flex;
      gap: cpx(24);
      align-items: center;
      justify-content: center;
    }
  }

  .reward {
    display: flex;
    flex-direction: column;
    align-items: center;

    .reward-icon {
      object-fit: contain;

      // Bottle art only fills ~77% of its canvas; match the coin and gem.
      &.icon-elixir {
        transform: scale(1.2);
      }
    }

    .reward-amount {
      font-weight: 700;
      font-size: cpx(40);
      line-height: 1;
      text-shadow: 0 cpx(3) cpx(6) rgba(0, 0, 0, 0.75);
    }
  }

  .secondary-action {
    position: absolute;
    left: calc((1391 - 61 + #{$safe-x}) / #{$canvas-w} * 100%);
    top: calc((800 + #{$button-dy} + 100) / #{$canvas-h} * 100%);
    width: calc(520 / #{$canvas-w} * 100%);
    z-index: 5;
    font-weight: 400;
    font-size: cpx(24);
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    // buttons.scss strips it from every `a` with !important.
    text-decoration: underline !important;
    color: white;
    cursor: pointer;

    &:hover {
      color: $text-accent;
    }

    &.disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .action-button {
    border: 0;
    border-radius: 0.35rem;
    background-color: #1d6fa5;
    color: var(--premium-button-title-color, white);
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  // 398 x 110 art stretched to 520 wide, centred on the 6.2 slot.
  .primary-action {
    position: absolute;
    left: calc((1330 + #{$safe-x}) / #{$canvas-w} * 100%);
    top: calc((800 + #{$button-dy}) / #{$canvas-h} * 100%);
    width: calc(520 / #{$canvas-w} * 100%);
    height: calc(110 / #{$canvas-h} * 100%);
    background: var(--button-bg, #1d6fa5) center / 100% 100% no-repeat;
    border-radius: 0;
    padding: 0 cpx(45);
    font-size: cpx(30);
    white-space: nowrap;
    text-transform: uppercase;
    text-shadow: 0 0.0625rem 0.2rem rgba(0, 0, 0, 0.5);

    &:hover:not(:disabled):not(.claimed-action) {
      background-image: var(--button-bg-hover, var(--button-bg));
    }
    &.claimed-action {
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.55;
      filter: grayscale(0.7);
      cursor: default;
      pointer-events: none;
    }

    // On the pill's top-right corner -- the art has transparent padding.
    .claim-bubble {
      top: calc(20 / 110 * 100% - 0.45rem);
      right: calc(15 / 398 * 100% - 0.45rem);
      width: 0.9rem;
      height: 0.9rem;
    }
  }

  // Global `.close-button`, shrunk to the info button's size.
  .close-button {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
  }
  // The art's solid circle is 56 of 72px (rest is glow), so 46px shows a
  // 36px circle, centred like the close button.
  .info-button {
    position: absolute;
    top: 7px;
    left: 7px;
    width: 46px;
    height: 46px;
    z-index: 10;
    background: url(~@/assets/img/buttons/btn-info-default.png) center / 100%
      100% no-repeat;
    cursor: pointer;

    &:hover {
      filter: drop-shadow(1px 0 3px #ffa900) drop-shadow(0 0 3px #ffa900);
    }
  }
}
</style>
